// 用户模块服务层

import utils, { AdUtils } from "../../../utils";
import { userApi } from "../api";
import { User, LoginRequest, RegisterRequest } from "../types";
import { LoginResponse, RegisterResponse } from "../types/auth.types";

// 用户服务类
export class UserService {
  private static instance: UserService;
  // 请求去重：存储当前正在进行的请求 Promise
  private pendingUserRequest: Promise<User> | null = null;
  // user_info 静态缓存（纯静态：资料/配置/auth/vip）。长缓存 + 事件失效：
  //  - 用户编辑(资料/绑定/密码) → 各编辑点 clearUserInfoCache
  //  - VIP 升级 → loadUserRealtime 检测 upgraded 后 clearUserInfoCache
  //  - 运营后台改配置(前端无事件) → 靠此 TTL 兜底刷新
  private staticUserCache: { data: User; ts: number } | null = null;
  private static readonly STATIC_USER_TTL = 15 * 60 * 1000;

  static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  // 用户认证服务
  async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      const result = await userApi.login(data);
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "login failed";
      throw new Error(errorMessage);
    }
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    try {
      const result = await userApi.register(data);
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "register failed";
      throw new Error(errorMessage);
    }
  }

  // 【共通】redirectTo 可选:登出后跳转目标,默认 "/" 保持既有行为。
  // 手机验证活动改绑登录号后需要直接去登录页重新登录(2026-09-08)。
  logout(redirectTo: string = "/"): void {
    this.clearUserInfoCache();
    utils.setUser(null);
    utils._clearObject();
    utils.deleteAllCookies();
    utils.setJustLoggedOut();
    window.location.replace(redirectTo);
  }

  // 用户数据服务（带节流和去重）
  async loadUserInfo(forceRefresh = false): Promise<User> {
    const now = Date.now();

    // 静态缓存：user_info 已是纯静态，TTL 内直接返回缓存，避免跨页面反复拉取
    if (
      !forceRefresh &&
      this.staticUserCache &&
      now - this.staticUserCache.ts < UserService.STATIC_USER_TTL
    ) {
      // 回传兜底与 user_info 缓存解耦：命中缓存也跑一次 checkPurchase。
      // paidList 为空时它立刻 return、不发网络，成本极低；避免 15 分钟缓存期内
      // 慢确认支付(如银行转账/PIX)的 FB/TT 购买回传因 checkPurchase 停摆而漏报。
      AdUtils.checkPurchase();
      return this.staticUserCache.data;
    }

    // 去重：已有正在进行的请求时，直接返回该请求（取消 api 层缓存后由此兜并发）
    if (this.pendingUserRequest) {
      return this.pendingUserRequest;
    }

    // 创建新请求
    this.pendingUserRequest = this._doLoadUserInfo();

    try {
      const result = await this.pendingUserRequest;
      AdUtils.checkPurchase();
      this.staticUserCache = { data: result, ts: Date.now() };
      return result;
    } finally {
      this.pendingUserRequest = null;
    }
  }

  /** 清除 user_info 静态缓存（登出、资料编辑、等级变化等需强制刷新时调用）。 */
  clearUserInfoCache(): void {
    this.staticUserCache = null;
  }

  // 实际执行加载用户信息的内部方法
  private async _doLoadUserInfo(): Promise<User> {
    try {
      const data = await userApi.getUserInfo();
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "load user info failed";
      throw new Error(errorMessage);
    }
  }

  // 实时态去重：在途请求共享
  private pendingRealtimeRequest: Promise<Partial<User>> | null = null;

  // 用户实时态（余额/任务奖励等）：仅在途去重，不做结果缓存（领奖/充值后需新鲜余额）
  async loadUserRealtime(): Promise<Partial<User>> {
    // 去重：有在途请求时直接返回该请求
    if (this.pendingRealtimeRequest) {
      return this.pendingRealtimeRequest;
    }
    this.pendingRealtimeRequest = userApi.getUserRealtime();
    try {
      return await this.pendingRealtimeRequest;
    } finally {
      this.pendingRealtimeRequest = null;
    }
  }

  // 红包状态
  async loadRedpacketStatus(): Promise<Partial<User>> {
    return userApi.getRedpacketStatus();
  }

  // 签到状态
  async loadUserCheckin(): Promise<Partial<User>> {
    return userApi.getUserCheckin();
  }

  // 游客数据服务
  async loadGuestInfo(): Promise<User> {
    try {
      const data = await userApi.getGuestInfo();
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "load guest info failed";
      throw new Error(errorMessage);
    }
  }

  // 更新用户余额
  async updateUserBalance(): Promise<{ balance: number; currency: string }> {
    try {
      const balanceData = await userApi.getUserBalance();
      return balanceData;
    } catch (error) {
      console.error("update user balance failed:", error);
      throw error;
    }
  }

  // 更新用户信息
  async updateUserInfo(data: Partial<User>): Promise<User> {
    try {
      const updatedUser = await userApi.updateUserInfo(data);
      this.clearUserInfoCache(); // 资料变更后失效静态缓存，下次 loadUserInfo 拉新
      return updatedUser;
    } catch (error) {
      console.error("update user info failed:", error);
      throw error;
    }
  }
}

// 创建单例实例
export const userService = UserService.getInstance();

// 导出便捷方法
export const {
  login,
  register,
  logout,
  loadUserInfo,
  loadUserRealtime,
  loadRedpacketStatus,
  loadUserCheckin,
  loadGuestInfo,
  updateUserBalance,
  updateUserInfo,
} = userService;
